import type { ReactElement } from "react";

import { Typography, Skeleton } from "@aviary";
import { ListGridSelector } from "@shared/components/ListGridSelector";
import { useBreakpoints, useCatalogSearch } from "@shared/hooks";

import { CatalogSortDropdown } from "../CatalogSortDropdown";

import * as styles from "./ProductResultsHeader.styles";

interface Props {
  title: string | (ReactElement | string)[];
  resultCount?: number;
  isLoadingNewResults?: boolean;
  isDrawer?: boolean;
}

const ProductResultsHeader = ({ title, resultCount, isLoadingNewResults, isDrawer }: Props) => {
  const { phoneLarge } = useBreakpoints();
  const { isGridView, setGridView } = useCatalogSearch();
  const isSortViewSelectorDisabled = !resultCount || resultCount <= 1;

  const renderTitle = () => {
    if (isLoadingNewResults) {
      return (
        <Skeleton type="typography" typographySize="h1" width={6} css={styles.titleSkeleton} />
      );
    }

    return (
      <Typography type="body" isMarginless css={styles.title} data-e2e="product-results-quantity">
        {title}
      </Typography>
    );
  };

  const renderSortDropdown = () => {
    if (phoneLarge.greaterThan) {
      if (isLoadingNewResults) {
        return (
          <Skeleton
            type="typography"
            typographySize="h1"
            width={8}
            css={styles.sortDropdownSkeleton}
          />
        );
      }

      return <CatalogSortDropdown isDisabled={isSortViewSelectorDisabled} />;
    }

    return null;
  };

  const renderGridSelector = () => {
    if (isDrawer) return null;
    if (phoneLarge.greaterThan) {
      if (isLoadingNewResults) {
        return (
          <Skeleton type="typography" typographySize="h1" width={6} css={styles.gridListSelector} />
        );
      }

      return (
        <ListGridSelector
          isDisabled={isSortViewSelectorDisabled}
          isGridView={isGridView}
          handleToggle={setGridView}
          css={styles.gridListSelector}
        />
      );
    }

    return null;
  };

  return (
    <div css={styles.header}>
      {renderTitle()}
      <div css={styles.actions}>
        {renderSortDropdown()}
        {renderGridSelector()}
      </div>
    </div>
  );
};

export { ProductResultsHeader };
